import React, { useCallback, useEffect, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useJoyride, STATUS } from 'react-joyride';
import '../styles/Tutorial.css';

/**
 * Poll DOM until selector appears (removal=false) or disappears (removal=true).
 */
function waitForElement(selector, removal = false, maxWait = 15000) {
  return new Promise(resolve => {
    const start = Date.now();
    const check = () => {
      const found = !!document.querySelector(selector);
      if (found !== removal) return resolve();
      if (Date.now() - start < maxWait) setTimeout(check, 200);
      else resolve();
    };
    check();
  });
}

function simulateTyping(selector, text) {
  const input = document.querySelector(selector);
  if (!input) return;
  input.focus();
  // React-select ignores native input/change events — it only responds to React's
  // synthetic events, which are triggered by nativeInputValueSetter + InputEvent.
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
  nativeInputValueSetter.call(input, text);
  input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: text }));
}

// Arrow sits at the top of the card when placement is 'bottom' — match the gradient header.
// All other placements: arrow is adjacent to the white body, use bg color.
const ARROW_COLOR_HEADER = '#ec671f'; // midpoint of plex-yellow→plex-orange gradient
const ARROW_COLOR_BG = '#ffffff';     // matches --bg-color in light mode

function buildJoyrideStep(cfg) {
  const placement = cfg.element ? (cfg.position || 'bottom') : 'center';
  const arrowColor = placement.startsWith('bottom') ? ARROW_COLOR_HEADER : ARROW_COLOR_BG;

  const step = {
    target: cfg.element || 'body',
    title: cfg.title,
    content: cfg.intro,
    placement,
    disableBeacon: true,
    skipBeacon: true,
    skipScroll: cfg.skipScroll || false,
    hideFooter: !!cfg.nextWhen,
    arrowColor,
    data: cfg,
  };

  if (cfg.waitFor || cfg.waitForRemoval) {
    step.before = async () => {
      if (cfg.waitForRemoval && cfg.element) await waitForElement(cfg.element, true);
      if (cfg.waitFor) await waitForElement(cfg.waitFor, false);
    };
    step.targetWaitTimeout = 30000;
    step.beforeTimeout = 30000;
  }

  if (cfg.clickElement) {
    step.after = () => {
      const el = document.querySelector(cfg.clickElement);
      if (el) el.click();
    };
  }

  return step;
}

/**
 * Custom tooltip that supports a disabled Next button for enableNextWhen steps.
 */
const PlexTooltip = ({ backProps, index, primaryProps, size, skipProps, step, tooltipProps, nextEnabled }) => {
  const cfg = step.data || {};
  const isLast = index === size - 1;

  return (
    <div {...tooltipProps} className="plex-joyride-tooltip">
      <div className="plex-joyride-header">
        <span className="plex-joyride-title">{step.title}</span>
        <button {...skipProps} className="plex-joyride-skip">Skip Tour</button>
      </div>
      <div className="plex-joyride-content">{step.content}</div>
      {!step.hideFooter && (
        <div className="plex-joyride-footer">
          {index > 0 && <button {...backProps} className="plex-joyride-btn-back">Back</button>}
          <button
            {...primaryProps}
            className="plex-joyride-btn-next"
            disabled={cfg.enableNextWhen ? !nextEnabled : false}
          >
            {isLast ? 'Finish' : 'Next'}
          </button>
        </div>
      )}
    </div>
  );
};

const TutorialRunner = ({ tutorialStore }) => {
  const { run, steps, stepIndex } = tutorialStore;
  const joyrideSteps = steps.map(buildJoyrideStep);
  const controlsRef = useRef(null);
  const [nextEnabled, setNextEnabled] = useState(false);

  // Reset nextEnabled whenever the step changes
  useEffect(() => {
    setNextEnabled(false);
  }, [stepIndex]);

  const tooltipComponent = useCallback(
    (props) => <PlexTooltip {...props} nextEnabled={nextEnabled} />,
    [nextEnabled]
  );

  const { Tour } = useJoyride({
    continuous: true,
    run,
    steps: joyrideSteps,
    stepIndex,
    debug: false,
    disableOverlayClose: true,
    hideBackButton: false,
    scrollToFirstStep: true,
    tooltipComponent,
    locale: { back: 'Back', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip Tour' },
    options: {
      primaryColor: 'var(--plex-orange)',
      overlayColor: 'rgba(0,0,0,0.6)',
      zIndex: 10000,
    },
    onEvent: (data, controls) => {
      const { action, index, status, type } = data;
      controlsRef.current = controls;

      if (type === 'step:after') {
        tutorialStore.onStepChange(index + (action === 'prev' ? -1 : 1));
      }
      if (type === 'error:target_not_found') {
        tutorialStore.onStepChange(index + 1);
      }
      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        tutorialStore.onTourEnd(status === STATUS.FINISHED);
      }
    },
  });

  // enableNextWhen: enable the Next button once the target element appears,
  // injecting text first if the input is empty.
  useEffect(() => {
    if (!run || !steps[stepIndex]) return;
    const cfg = steps[stepIndex];
    if (!cfg.enableNextWhen) return;

    let cancelled = false;

    if (!document.querySelector(cfg.enableNextWhen) && cfg.typeIfEmpty) {
      const input = document.querySelector(cfg.typeIfEmpty.selector);
      if (input && !input.value) {
        simulateTyping(cfg.typeIfEmpty.selector, cfg.typeIfEmpty.text);
      }
    }

    waitForElement(cfg.enableNextWhen, false).then(() => {
      if (!cancelled) setNextEnabled(true);
    });


    return () => { cancelled = true; };
  }, [run, stepIndex, steps]);

  // nextWhen: auto-advance when the target element appears (no Next button shown)
  useEffect(() => {
    if (!run || !steps[stepIndex]) return;
    const cfg = steps[stepIndex];
    if (!cfg.nextWhen) return;

    let cancelled = false;
    waitForElement(cfg.nextWhen, false).then(() => {
      if (!cancelled && controlsRef.current) controlsRef.current.next();
    });

    return () => { cancelled = true; };
  }, [run, stepIndex, steps]);

  if (!run) return null;
  return <>{Tour}</>;
};

export default inject('tutorialStore')(observer(TutorialRunner));